import { AlertCode } from 'shared/types/checkout';
import { getCookie } from 'utils/cookies';
import { DEFAULT_CURRENCY } from 'utils/currency';

/**
 * Reads the marketplace FX quote cookie used to seed locked conversion context.
 * Shared by pay-in context fetch and Stripe validate so Elements and PaymentIntent
 * resolve against the same quote when storage is stale.
 */
export const readMarketplaceFxQuote = ():
  | Record<string, unknown>
  | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  try {
    const rawFxCookie = getCookie('edx_fx_quote');
    if (!rawFxCookie) {
      return undefined;
    }
    const parsed = JSON.parse(rawFxCookie) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Cookie access or parsing failed — proceed without marketplace quote
  }
  return undefined;
};

export const isCartVersionConflictError = (error: string): boolean =>
  /concurrent\s*modification/i.test(error) ||
  /different version than expected/i.test(error);

/**
 * A pay-in failure is only worth telling the shopper about when it degraded a
 * local-currency payment back to the cart currency. Carts already settling in
 * USD fall back to the same amount they were shown, so an FX alert would be wrong.
 */
export const shouldSurfacePayInContextAlert = (
  error: string | null,
  payInCurrency?: string,
): boolean => {
  if (
    !error ||
    !payInCurrency ||
    payInCurrency.toUpperCase() === DEFAULT_CURRENCY
  ) {
    return false;
  }

  return !isCartVersionConflictError(error);
};

/** Maps pay-in context fetch failures onto existing checkout alert codes. */
export const resolvePayInContextAlertCode = (error: string): AlertCode => {
  const normalized = error.toLowerCase();
  if (
    normalized.includes('quote_expired') ||
    normalized.includes('quote_missing') ||
    normalized.includes('quote expired') ||
    normalized.includes('locked fx quote required')
  ) {
    return AlertCode.QUOTE_EXPIRED;
  }
  return AlertCode.RATES_UNAVAILABLE;
};
